import React, { useCallback, useEffect, useState } from "react";

import MainBody from "../../components/MainBody/MainBody";
import MainTabList from "../../components/MainBody/MainTabList";
import { useLocation, useNavigate } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  fileUpload,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import Pagination from "../../components/Pagination";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import AddEditChartOfAccount from "./AddEditChartOfAccount";
import Confirmation from "../../components/Confirmation";
import ChartOfAccountActionButton from "./ChartOfAccountActionButton";
import UploadChartOfAccount from "./UploadChartOfAccount";
import sortCaret from "../../components/SortCaret";
import DropDownButton from "../../components/DropDownButton";
// import SyncXero from "../../components/SyncXero";
import { useTrust } from "../../lib/context/TrustContext";

const ChartOfAccounts = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const selectedTab = query.get("tab") || "expense";
  const [loading, setLoading] = useState(false);
  const [accountTypesList, setAccountTypesList] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState("");
  const [searchParamData, setSearchParamData] = useState("");

  const defaultActiveKey =
    accountTypesList?.length > 0 &&
    accountTypesList?.find((item) => item?.tabName === selectedTab)?.tabValue;

  const [activeTab, setActiveTab] = useState(defaultActiveKey || 1);
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [edit, setEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [chartOfAccountId, setChartOfAccountId] = useState(null);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [accountData, setAccountData] = useState([]);
  const { trustId } = useTrust();
  // const builderId = localStorage.getItem("builderId")
  //   ? localStorage.getItem("builderId")
  //   : "";

  const [isRestore, setIsRestore] = useState(false);
  const [showRestore, setShowRestore] = useState(false);

  const [isDeleteAll, setIsDeleteAll] = useState(false);
  const [showDeleteAll, setShowDeleteAll] = useState(false);

  const [showAddEditChartOfAccount, setShowAddEditChartOfAccount] =
    useState(false);

  const [showChartOfAccountDelete, setShowChartOfAccountDelete] =
    useState(false);

  const fetchAccountTypes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await get(`${API.GET_ACCOUNT_TYPES}`);
      setLoading(false);
      const accountTypesList = data.data;
      let listing = accountTypesList?.map((item, index) => {
        const { name, id, ...rest } = item;
        return {
          name,
          tabValue: index + 1,
          id,
          tabName: name.split(" ").join("-").toLowerCase(),
          ...rest,
        };
      });
      setAccountTypesList(listing);
      return data.data;
    } catch (error) {
      setLoading(false);
      setAccountTypesList([]);
    }
  }, []);

  useEffect(() => {
    fetchAccountTypes();
  }, [fetchAccountTypes]);

  useEffect(() => {
    setActiveTab(
      accountTypesList?.find((item) => item?.tabName === selectedTab)?.tabValue
    );
  }, [accountTypesList, selectedTab]);

  useEffect(() => {
    setActiveAccountId(
      accountTypesList?.find((item) => item?.tabName === selectedTab)?.id
    );
  }, [activeTab, accountTypesList, selectedTab]);

  useEffect(() => {
    if (accountTypesList?.length > 0) {
      navigate(
        `?tab=${
          accountTypesList?.find((item) => item?.tabValue === activeTab)
            ?.tabName
            ? accountTypesList?.find((item) => item?.tabValue === activeTab)
                ?.tabName
            : "expenses"
        }&limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }`
      );
    }
  }, [activeTab, accountTypesList, limit, navigate, page]);

  const toggleShowAddEditChartOfAccount = () => {
    if (showAddEditChartOfAccount) {
      setEdit(false);
      setChartOfAccountId(null);
    }
    setShowAddEditChartOfAccount(!showAddEditChartOfAccount);
  };

  const fetchAccountData = useCallback(
    async (passLimit, passPage) => {
      if (trustId && activeAccountId) {
        try {
          setLoading(true);
          setAccountData([]);
          const { data } = await get(
            `${API.GET_ACCOUNT_DATA}?trust_id=${
              trustId ? trustId : ""
            }&account_type_id=${
              activeAccountId ? activeAccountId : ""
            }&search=${searchParam ? searchParam : ""}&limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&sort_column=${
              sortField ? sortField : ""
            }&sort_order=${sortOrder ? sortOrder : ""}`
          );
          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          // let accountList = data?.data?.data;
          // let notSelectedIds = [];
          // for (let i = 0; i < accountList?.length; i++) {
          //   notSelectedIds.push(accountList[i]?.id);
          // }
          // setNotXeroSelectedRowIds(notSelectedIds);
          setAccountData(data?.data?.data);
        } catch (error) {
          setLoading(false);
          setAccountData([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortField, sortOrder, activeAccountId]
  );

  const toggleShowDelete = () => {
    if (showChartOfAccountDelete) {
      setChartOfAccountId(null);
    }
    setShowChartOfAccountDelete(!showChartOfAccountDelete);
  };

  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchAccountData(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchAccountData(limit, page);
  };

  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "code",
      text: "TrustSoft Code",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "sub_code",
      text: "TrustSoft Sub Code",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "type",
      text: "Type",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "tax_rate",
      text: "Tax Rate",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "accounting_code",
      text: "Accounting Software Code",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "import_from",
      text: "Import From",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "97px" },
      headerClasses: THClasses,
      classes: TDClasses,
    },
  ];

  const taxListArray = [
    { label: "GST on Income", value: "1" },
    { label: "GST Free Expenses", value: "0" },
    { label: "GST on Expenses", value: "2" },
    { label: "BAS Excluded", value: "3" },
  ];

  const accountsDataGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      let disableparent = [];
      let childNodes = [];
      if (accountData[i]?.sub_code === null) {
        let parentItem = accountData?.filter(
          (item) =>
            item?.code === accountData[i]?.code && item?.sub_code === null
        );
        if (parentItem) {
          childNodes = accountData?.filter(
            (item) => item?.code === parentItem[0]?.code && item?.sub_code
          );
          for (let i = 0; i < childNodes?.length; i++) {
            disableparent.push(childNodes[i]?.is_used === 1);
          }
        }
      }
      items.push({
        key: accountData[i]?.id,
        name: accountData[i]?.name,
        type: accountData[i]?.type === 0 ? "Debit" : "Credit",
        tax_rate: taxListArray?.find(
          (item) => Number(item?.value) === Number(accountData[i]?.tax)
        )?.label,
        code: accountData[i]?.code ? accountData[i]?.code : "-",
        sub_code: accountData[i]?.sub_code ? accountData[i]?.sub_code : "-",
        accounting_code: accountData[i]?.accounting_code
          ? accountData[i]?.accounting_code
          : "-",
        import_from: accountData[i]?.import_from
          ? accountData[i]?.import_from
          : "TrustSoft",
        action: (
          <>
            {(activeTab === 1 || activeTab === 2) && (
              <ChartOfAccountActionButton
                id={accountData[i]?.id}
                setEdit={setEdit}
                setChartOfAccountId={setChartOfAccountId}
                toggleShowAddEditChartOfAccount={
                  toggleShowAddEditChartOfAccount
                }
                toggleShowDelete={toggleShowDelete}
              />
            )}
          </>
        ),
      });
    }
    return items;
  };

  const accountsData = accountsDataGenerator(accountData?.length);

  let singleSelectedRows = [];
  let allSelectedRows = [];

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    classes: TDClasses,
    nonSelectable: ["not-select"],
    selected: selectedRow,
    nonSelectableClasses: "not-selected-class",
    onSelect: (row, isSelect, rowIndex, e) => {
      singleSelectedRows = [...selectedRow];
      if (isSelect) {
        let selectParentRow = accountData?.find((item) => item.id === row?.key);
        if (selectParentRow?.sub_code) {
          singleSelectedRows = [...selectedRow, row?.key];
        } else {
          let childCOA = accountData?.filter(
            (i) => i.code === selectParentRow?.code && i?.sub_code
          );
          let childkeys = [];
          for (let i = 0; i < childCOA?.length; i++) {
            childkeys.push(selectedRow.includes(childCOA[i]?.id));
          }
          // if (!childkeys.includes(false)) {
          singleSelectedRows = [...selectedRow, row?.key];
          // }
        }
      } else {
        singleSelectedRows.splice(selectedRow.indexOf(row?.key), 1);
      }
      setSelectedRow(singleSelectedRows);
    },
    onSelectAll: (isSelect, rows, e) => {
      if (accountData?.length > 0) {
        if (isSelect) {
          for (let i = 0; i < rows.length; i++) {
            if (selectedRow.includes(rows[i].key)) {
            } else {
              allSelectedRows.push(rows[i].key);
            }
          }
        } else {
          for (let i = 0; i < rows.length; i++) {
            if (selectedRow.includes(rows[i].key)) {
              selectedRow.splice(selectedRow.indexOf(rows[i].key), 1);
            }
          }
        }
        setSelectedRow([...selectedRow, ...allSelectedRows]);
      }
    },
  };

  // Single COA Delete
  const handleChartOfAccountDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ACCOUNT_DELETE}/${chartOfAccountId}`);
      setLoading(false);
      fetchAccountData();
    } catch (error) {
      setLoading(false);
    }
  };

  // Restore the Default COA
  const toggleRestoreModal = () => {
    setShowRestore(!showRestore);
  };

  const handleRestore = async () => {
    try {
      setIsRestore(true);
      const { data } = await get(
        `${API.ACCOUNT_RESTORE}?trust_id=${trustId ? trustId : ""}`
      );
      if (data) {
        setIsRestore(false);
        fetchAccountData(limit, page);
        toggleRestoreModal();
      }
    } catch (e) {
      setIsRestore(false);
    }
  };

  // Delete all not used COA
  const toggleDeleteAllModal = () => {
    setShowDeleteAll(!showDeleteAll);
  };

  const handleDeleteAll = async () => {
    const formData = new FormData();
    if (selectedRow?.length > 0) {
      for (let i = 0; i < selectedRow?.length; i++) {
        formData.append(`ids[${i}]`, selectedRow[i]);
      }
    }
    formData.append("trust_id", trustId);

    try {
      setIsDeleteAll(true);
      const { data } = await fileUpload(`${API.ACCOUNT_DELETE_ALL}`, formData);
      setIsDeleteAll(false);
      if (data) {
        toggleDeleteAllModal();
        setSelectedRow([]);
        fetchAccountData(limit, page);
      }
    } catch (error) {
      setIsDeleteAll(false);
    }
  };

  return (
    <div>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Import Chart Of Account */}
            {/* <SyncXero
              builderId={builderId}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              trustId={trustId}
              fetchData={() => fetchAccountData(limit, page)}
              recordId={chartOfAccountId}
              setRecordId={setChartOfAccountId}
              module="chart_of_account_ids"
              api={API.COA_SYNC}
            /> */}
            {(activeTab === 1 || activeTab === 2) && (
              <>
                <UploadChartOfAccount
                  accountTypesList={accountTypesList}
                  fetchAccountData={() => fetchAccountData(limit, page)}
                  trustId={trustId}
                  ButtonName={"Import"}
                  setLoading={setLoading}
                />
                {/* Add Chart of Account */}
                <ResponsiveDrawerForm
                  description={""}
                  title={
                    edit ? "Edit Chart Of Account" : "Add Chart Of Account"
                  }
                  handleOpen={() => {}}
                  handleClose={() => {
                    setEdit(false);
                    setChartOfAccountId(null);
                    setShowAddEditChartOfAccount(false);
                  }}
                  write={true}
                  buttonText={
                    <div className="flex items-center gap-1">
                      <LucidePlusCircle size={18} />
                      <span>Add Chart of Account</span>
                    </div>
                  }
                  className={""}
                  open={showAddEditChartOfAccount}
                  buttonClassName={""}
                  setOpen={setShowAddEditChartOfAccount}
                >
                  <AddEditChartOfAccount
                    edit={edit}
                    toggleShowAddEditChartOfAccount={
                      toggleShowAddEditChartOfAccount
                    }
                    id={chartOfAccountId}
                    accountId={activeAccountId}
                    accountTypesList={accountTypesList}
                    fetchAccountData={() => fetchAccountData(limit, page)}
                    trustId={trustId}
                  />
                </ResponsiveDrawerForm>
              </>
            )}

            {/* Action */}
            <DropDownButton
              items={[
                {
                  label: "Restore",
                  onClick: () => toggleRestoreModal(),
                  disabled: isRestore,
                },
                {
                  label:
                    selectedRow?.length > 0 ? "Delete Selected" : "Delete All",
                  onClick: () => toggleDeleteAllModal(),
                  disabled:
                    accountData?.length === 0
                      ? true
                      : isDeleteAll
                      ? true
                      : false,
                  danger: true,
                },
              ]}
            />
          </div>
        }
        mainClassName="w-[calc(100%-3rem)]"
        loading={loading || isRestore || isDeleteAll}
      >
        <MainTabList
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabsKey={accountTypesList}
          parentClass={false}
        >
          {
            <div className="custom-table tab-table">
              <div className="table-body-class max-h-[674px]">
                <BootstrapTable
                  keyField="key"
                  remote
                  data={accountsData}
                  columns={columns}
                  onTableChange={handleTableChange}
                  selectRow={selectRow}
                  rowClasses={TRClasses}
                  noDataIndication="No Data Found"
                  classes={TableClasses}
                  wrapperClasses={TableWrapClasses}
                />
              </div>

              <Pagination
                total={total}
                limit={parseInt(limit)}
                currentPage={page}
                updateLimit={handleUpdateLimit}
                updatePage={handleChangePage}
                from={from}
                to={to}
              />
            </div>
          }
        </MainTabList>
      </MainBody>

      <Confirmation
        title={"Delete Chart Of Account"}
        open={showChartOfAccountDelete}
        setOpen={setShowChartOfAccountDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete chart of account?"}
        okAction={async () => {
          handleChartOfAccountDelete();
          toggleShowDelete();
          fetchAccountData();
        }}
        cancelAction={() => {
          setChartOfAccountId(null);
          setShowChartOfAccountDelete(!showChartOfAccountDelete);
        }}
      />

      <Confirmation
        title={"Delete All Chart Of Account"}
        open={showDeleteAll}
        setOpen={setShowDeleteAll}
        toggleShow={() => {
          toggleDeleteAllModal();
        }}
        className="sm:max-w-1/2"
        message={`Are you sure you want to delete 
            ${
              selectedRow?.length === 0 ? "all" : "selected"
            } chart of account?`}
        okAction={async () => {
          handleDeleteAll();
        }}
        cancelAction={() => {
          toggleDeleteAllModal();
        }}
      />

      <Confirmation
        title={"Restore Chart Of Accounts"}
        open={showRestore}
        setOpen={setShowRestore}
        toggleShow={() => {
          toggleRestoreModal();
        }}
        className="sm:max-w-1/2"
        message={`Are you sure you want to restore chart of accounts?`}
        okAction={async () => {
          handleRestore();
        }}
        cancelAction={() => {
          toggleRestoreModal();
        }}
      />
    </div>
  );
};

export default ChartOfAccounts;
